import type { ClientAccessScope, ClientRecordAccess } from "./contracts";

export type PlanType =
  | "safety_plan"
  | "exposure_hierarchy"
  | "relapse_prevention"
  | "cope_ahead";

export interface PlanDocument {
  id: string;
  planType: PlanType;
  displayName: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number | null;
  status: string;
  isCurrent: boolean;
  source: string;
  storageAvailable: boolean;
  indexed: boolean;
  errorMessage: string;
  readyAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PatientPlanDocumentSummary {
  id: string;
  accessCode: string;
  nickname: string;
  exposureHierarchyText: string;
}

export interface PlanDocumentsListResponse {
  apiVersion: "1";
  success: true;
  authorized: true;
  found: true;
  operation: "planDocuments.list";
  db: "supabase";
  patient: PatientPlanDocumentSummary;
  planDocuments: PlanDocument[];
  access: ClientRecordAccess;
  error: string;
}

export interface ExposureHierarchyTextUpdateResponse {
  apiVersion: "1";
  success: true;
  authorized: true;
  found: true;
  operation: "planDocuments.updateExposureHierarchyText";
  db: "supabase";
  patient: PatientPlanDocumentSummary;
  updated: true;
  error: string;
}

export interface UploadPlanDocumentInput {
  clinicianCode: string;
  patientId: string;
  planType: PlanType;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentBase64: string;
}

export interface PlanDocumentUploadResponse {
  apiVersion: "1";
  success: true;
  authorized: true;
  found: true;
  operation: "planDocuments.upload";
  db: "supabase";
  uploaded: true;
  replaced: boolean;
  document: PlanDocument;
  error: string;
}

export interface PlanDocumentDeleteResponse {
  apiVersion: "1";
  success: true;
  authorized: true;
  found: true;
  operation: "planDocuments.delete";
  db: "supabase";
  deleted: boolean;
  alreadyEmpty: boolean;
  planType: PlanType;
  documentId: string;
  cleanupWarnings: string[];
  error: string;
}

export type ClientPlanDocumentsApiErrorCode =
  | "NOT_CONFIGURED"
  | "NOT_AUTHORIZED"
  | "NOT_FOUND"
  | "INVALID_REQUEST"
  | "INVALID_RESPONSE"
  | "NETWORK_ERROR"
  | "UPSTREAM_ERROR";

export interface ClientPlanDocumentsApiErrorState {
  code: ClientPlanDocumentsApiErrorCode;
  message: string;
  retryable: boolean;
}

export class ClientPlanDocumentsApiError extends Error {
  readonly code: ClientPlanDocumentsApiErrorCode;
  readonly retryable: boolean;

  constructor(
    message: string,
    code: ClientPlanDocumentsApiErrorCode,
    retryable = false,
  ) {
    super(message);
    this.name = "ClientPlanDocumentsApiError";
    this.code = code;
    this.retryable = retryable;
  }
}

interface ClientPlanDocumentsApiOptions {
  endpoint?: string;
  fetchImpl?: typeof fetch;
}

export interface ClientPlanDocumentsApi {
  list(
    clinicianCode: string,
    patientId: string,
    signal?: AbortSignal,
    accessScope?: ClientAccessScope,
  ): Promise<PlanDocumentsListResponse>;
  updateExposureHierarchyText(
    clinicianCode: string,
    patientId: string,
    exposureHierarchyText: string,
    signal?: AbortSignal,
  ): Promise<ExposureHierarchyTextUpdateResponse>;
  uploadPlanDocument(
    input: UploadPlanDocumentInput,
    signal?: AbortSignal,
  ): Promise<PlanDocumentUploadResponse>;
  deletePlanDocument(
    clinicianCode: string,
    patientId: string,
    planType: PlanType,
    signal?: AbortSignal,
  ): Promise<PlanDocumentDeleteResponse>;
}

const DEFAULT_ENDPOINT =
  "https://educationforall.app.n8n.cloud/webhook/client-plan-documents-api-v1";

const planTypes: readonly PlanType[] = [
  "safety_plan",
  "exposure_hierarchy",
  "relapse_prevention",
  "cope_ahead",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredString(
  value: unknown,
  field: string,
  allowEmpty = true,
): string {
  if (typeof value !== "string" || (!allowEmpty && !value.trim())) {
    throw invalidResponse(field);
  }
  return value;
}

function requiredBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") throw invalidResponse(field);
  return value;
}

function requiredStringArray(value: unknown, field: string): string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
    throw invalidResponse(field);
  }
  return value;
}

function optionalStringArray(value: unknown, field: string): string[] {
  if (value === undefined) return [];
  return requiredStringArray(value, field);
}

function nullableNonnegativeNumber(
  value: unknown,
  field: string,
): number | null {
  if (value === null) return null;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < 0
  ) {
    throw invalidResponse(field);
  }
  return value;
}

function invalidResponse(field?: string): ClientPlanDocumentsApiError {
  return new ClientPlanDocumentsApiError(
    field
      ? `The Client Plan Documents API response is invalid at ${field}.`
      : "The Client Plan Documents API returned an unexpected response.",
    "INVALID_RESPONSE",
    false,
  );
}

function configuredEndpoint(): string {
  return (
    process.env.REACT_APP_CLIENT_PLAN_DOCUMENTS_API_URL?.trim() ||
    DEFAULT_ENDPOINT
  );
}

function assertRequestValue(value: string, label: string): string {
  const normalized = value.trim();
  if (!normalized) {
    throw new ClientPlanDocumentsApiError(
      `${label} is required.`,
      "INVALID_REQUEST",
      false,
    );
  }
  return normalized;
}

function assertPlanType(value: string, field: string): PlanType {
  if (!planTypes.includes(value as PlanType)) throw invalidResponse(field);
  return value as PlanType;
}

function assertRequestPlanType(value: PlanType): PlanType {
  if (!planTypes.includes(value)) {
    throw new ClientPlanDocumentsApiError(
      "Plan type is invalid.",
      "INVALID_REQUEST",
      false,
    );
  }
  return value;
}

function stripBase64DataUrlPrefix(value: string): string {
  return value.replace(/^data:[^,]*;base64,/i, "");
}

function parsePatient(value: unknown): PatientPlanDocumentSummary {
  if (!isRecord(value)) throw invalidResponse("patient");
  return {
    id: requiredString(value.id, "patient.id", false),
    accessCode: requiredString(value.accessCode, "patient.accessCode"),
    nickname: requiredString(value.nickname, "patient.nickname"),
    exposureHierarchyText: requiredString(
      value.exposureHierarchyText,
      "patient.exposureHierarchyText",
    ),
  };
}

function parseAccess(value: unknown): ClientRecordAccess {
  if (value === undefined) {
    return { scope: "assigned", readOnly: false };
  }
  if (!isRecord(value)) throw invalidResponse("access");

  const scope = requiredString(value.scope, "access.scope", false);
  if (scope !== "assigned" && scope !== "team") {
    throw invalidResponse("access.scope");
  }

  return {
    scope,
    readOnly: requiredBoolean(value.readOnly, "access.readOnly"),
  };
}

function parsePlanDocument(value: unknown, index: number): PlanDocument {
  if (!isRecord(value)) throw invalidResponse(`planDocuments[${index}]`);
  const planType = requiredString(
    value.planType,
    `planDocuments[${index}].planType`,
    false,
  );
  const parsedPlanType = assertPlanType(
    planType,
    `planDocuments[${index}].planType`,
  );

  return {
    id: requiredString(value.id, `planDocuments[${index}].id`, false),
    planType: parsedPlanType,
    displayName: requiredString(
      value.displayName,
      `planDocuments[${index}].displayName`,
      false,
    ),
    originalFilename: requiredString(
      value.originalFilename,
      `planDocuments[${index}].originalFilename`,
    ),
    mimeType: requiredString(
      value.mimeType,
      `planDocuments[${index}].mimeType`,
    ),
    sizeBytes: nullableNonnegativeNumber(
      value.sizeBytes,
      `planDocuments[${index}].sizeBytes`,
    ),
    status: requiredString(
      value.status,
      `planDocuments[${index}].status`,
      false,
    ),
    isCurrent: requiredBoolean(
      value.isCurrent,
      `planDocuments[${index}].isCurrent`,
    ),
    source: requiredString(
      value.source,
      `planDocuments[${index}].source`,
    ),
    storageAvailable: requiredBoolean(
      value.storageAvailable,
      `planDocuments[${index}].storageAvailable`,
    ),
    indexed: requiredBoolean(
      value.indexed,
      `planDocuments[${index}].indexed`,
    ),
    errorMessage: requiredString(
      value.errorMessage,
      `planDocuments[${index}].errorMessage`,
    ),
    readyAt: requiredString(
      value.readyAt,
      `planDocuments[${index}].readyAt`,
    ),
    createdAt: requiredString(
      value.createdAt,
      `planDocuments[${index}].createdAt`,
    ),
    updatedAt: requiredString(
      value.updatedAt,
      `planDocuments[${index}].updatedAt`,
    ),
  };
}

function assertSuccessfulBase(
  value: unknown,
  operation:
    | "planDocuments.list"
    | "planDocuments.updateExposureHierarchyText"
    | "planDocuments.upload"
    | "planDocuments.delete",
): Record<string, unknown> {
  if (!isRecord(value)) throw invalidResponse();
  if (value.apiVersion !== "1") throw invalidResponse("apiVersion");
  if (value.operation !== operation) throw invalidResponse("operation");
  if (value.db !== "supabase") throw invalidResponse("db");
  if (typeof value.success !== "boolean") throw invalidResponse("success");
  if (typeof value.authorized !== "boolean") {
    throw invalidResponse("authorized");
  }
  if (typeof value.found !== "boolean") throw invalidResponse("found");
  requiredString(value.error, "error");

  if (!value.authorized) {
    throw new ClientPlanDocumentsApiError(
      "This clinician is not authorized to view the requested client.",
      "NOT_AUTHORIZED",
      false,
    );
  }
  if (!value.found) {
    throw new ClientPlanDocumentsApiError(
      "The requested client could not be found.",
      "NOT_FOUND",
      false,
    );
  }
  if (!value.success) {
    throw new ClientPlanDocumentsApiError(
      "Client plan documents are temporarily unavailable.",
      "UPSTREAM_ERROR",
      true,
    );
  }
  return value;
}

function parseUploadResponse(value: unknown): PlanDocumentUploadResponse {
  const record = assertSuccessfulBase(value, "planDocuments.upload");
  if (record.uploaded !== true) throw invalidResponse("uploaded");
  const replaced = requiredBoolean(record.replaced, "replaced");
  const document = parsePlanDocument(record.document, 0);
  return {
    apiVersion: "1",
    success: true,
    authorized: true,
    found: true,
    operation: "planDocuments.upload",
    db: "supabase",
    uploaded: true,
    replaced,
    document,
    error: requiredString(record.error, "error"),
  };
}

function parseDeleteResponse(value: unknown): PlanDocumentDeleteResponse {
  const record = assertSuccessfulBase(value, "planDocuments.delete");
  const deleted = requiredBoolean(record.deleted, "deleted");
  const alreadyEmpty = requiredBoolean(record.alreadyEmpty, "alreadyEmpty");
  if (!deleted && !alreadyEmpty) throw invalidResponse("deleted");
  const planType = assertPlanType(
    requiredString(record.planType, "planType", false),
    "planType",
  );
  return {
    apiVersion: "1",
    success: true,
    authorized: true,
    found: true,
    operation: "planDocuments.delete",
    db: "supabase",
    deleted,
    alreadyEmpty,
    planType,
    documentId:
      record.documentId === undefined
        ? ""
        : requiredString(record.documentId, "documentId"),
    cleanupWarnings: optionalStringArray(
      record.cleanupWarnings,
      "cleanupWarnings",
    ),
    error: requiredString(record.error, "error"),
  };
}

function parseListResponse(value: unknown): PlanDocumentsListResponse {
  const record = assertSuccessfulBase(value, "planDocuments.list");
  if (!Array.isArray(record.planDocuments)) {
    throw invalidResponse("planDocuments");
  }
  return {
    apiVersion: "1",
    success: true,
    authorized: true,
    found: true,
    operation: "planDocuments.list",
    db: "supabase",
    patient: parsePatient(record.patient),
    planDocuments: record.planDocuments.map(parsePlanDocument),
    access: parseAccess(record.access),
    error: requiredString(record.error, "error"),
  };
}

function parseUpdateResponse(
  value: unknown,
): ExposureHierarchyTextUpdateResponse {
  const record = assertSuccessfulBase(
    value,
    "planDocuments.updateExposureHierarchyText",
  );
  if (record.updated !== true) throw invalidResponse("updated");
  return {
    apiVersion: "1",
    success: true,
    authorized: true,
    found: true,
    operation: "planDocuments.updateExposureHierarchyText",
    db: "supabase",
    patient: parsePatient(record.patient),
    updated: true,
    error: requiredString(record.error, "error"),
  };
}

export function createClientPlanDocumentsApi(
  options: ClientPlanDocumentsApiOptions = {},
): ClientPlanDocumentsApi {
  const endpoint = options.endpoint?.trim() || configuredEndpoint();
  const fetchImpl = options.fetchImpl;

  async function request<TResponse>(
    body: Record<string, unknown>,
    parse: (value: unknown) => TResponse,
    signal?: AbortSignal,
  ): Promise<TResponse> {
    if (!endpoint) {
      throw new ClientPlanDocumentsApiError(
        "The Client Plan Documents API endpoint is not configured.",
        "NOT_CONFIGURED",
        false,
      );
    }
    const executeFetch =
      fetchImpl ??
      (typeof window !== "undefined" && typeof window.fetch === "function"
        ? window.fetch.bind(window)
        : undefined);
    if (!executeFetch) {
      throw new ClientPlanDocumentsApiError(
        "The Client Plan Documents API transport is unavailable.",
        "NETWORK_ERROR",
        true,
      );
    }

    let response: Response;
    try {
      response = await executeFetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      throw new ClientPlanDocumentsApiError(
        "The Client Plan Documents API could not be reached.",
        "NETWORK_ERROR",
        true,
      );
    }

    let value: unknown;
    try {
      value = await response.json();
    } catch {
      throw new ClientPlanDocumentsApiError(
        "The Client Plan Documents API returned an unreadable response.",
        response.ok ? "INVALID_RESPONSE" : "UPSTREAM_ERROR",
        !response.ok,
      );
    }

    if (!response.ok) {
      throw new ClientPlanDocumentsApiError(
        "The Client Plan Documents API returned an unsuccessful response.",
        "UPSTREAM_ERROR",
        response.status >= 500,
      );
    }
    return parse(value);
  }

  return {
    list(clinicianCode, patientId, signal, accessScope) {
      return request(
        {
          apiVersion: "1",
          operation: "planDocuments.list",
          db: "supabase",
          clinicianCode: assertRequestValue(clinicianCode, "Clinician code"),
          patientId: assertRequestValue(patientId, "Patient ID"),
          ...(accessScope ? { accessScope } : {}),
        },
        parseListResponse,
        signal,
      );
    },
    updateExposureHierarchyText(
      clinicianCode,
      patientId,
      exposureHierarchyText,
      signal,
    ) {
      return request(
        {
          apiVersion: "1",
          operation: "planDocuments.updateExposureHierarchyText",
          db: "supabase",
          clinicianCode: assertRequestValue(clinicianCode, "Clinician code"),
          patientId: assertRequestValue(patientId, "Patient ID"),
          exposureHierarchyText,
        },
        parseUpdateResponse,
        signal,
      );
    },
    uploadPlanDocument(input, signal) {
      const sizeBytes = input.sizeBytes;
      if (!Number.isFinite(sizeBytes) || sizeBytes < 0) {
        throw new ClientPlanDocumentsApiError(
          "File size is invalid.",
          "INVALID_REQUEST",
          false,
        );
      }
      const contentBase64 = assertRequestValue(
        stripBase64DataUrlPrefix(input.contentBase64),
        "File content",
      );
      return request(
        {
          apiVersion: "1",
          operation: "planDocuments.upload",
          db: "supabase",
          clinicianCode: assertRequestValue(
            input.clinicianCode,
            "Clinician code",
          ),
          patientId: assertRequestValue(input.patientId, "Patient ID"),
          planType: assertRequestPlanType(input.planType),
          filename: assertRequestValue(input.filename, "Filename"),
          mimeType: assertRequestValue(input.mimeType, "MIME type"),
          sizeBytes,
          contentBase64,
        },
        parseUploadResponse,
        signal,
      );
    },
    deletePlanDocument(clinicianCode, patientId, planType, signal) {
      return request(
        {
          apiVersion: "1",
          operation: "planDocuments.delete",
          db: "supabase",
          clinicianCode: assertRequestValue(clinicianCode, "Clinician code"),
          patientId: assertRequestValue(patientId, "Patient ID"),
          planType: assertRequestPlanType(planType),
        },
        parseDeleteResponse,
        signal,
      );
    },
  };
}

export const clientPlanDocumentsApi = createClientPlanDocumentsApi();
